import Papa from 'papaparse';
import { read, utils } from 'xlsx';

import { ColumnMapping, CompedTicketData, ExpectedField } from './types';

export const fileTypes = {
	excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	csv: 'text/csv',
};

export const FIXED_HEADERS = ['Email', 'First Name', 'Last Name', 'Quantity', 'Ticket Type'];

/** Expected fields in the order shown by the column-matching UI (BulkColumnMapping). */
export const EXPECTED_FIELDS: { field: ExpectedField; label: string }[] = [
	{ field: 'Email', label: 'Email' },
	{ field: 'First Name', label: 'First Name' },
	{ field: 'Last Name', label: 'Last Name' },
	{ field: 'Ticket Type', label: 'Ticket Type' },
	{ field: 'Quantity', label: 'Quantity' },
];

export const formatBytes = (bytes: number, decimals = 2): string => {
	if (!Number.isFinite(bytes) || bytes <= 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

export const isValidEmail = (email: string): boolean => {
	const emailRegex =
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
	return emailRegex.test(email.trim());
};

export const isNumeric = (str: string): boolean => !isNaN(Number(str.trim())) && str.trim() !== '';

export const detectEmailColumn = (data: CompedTicketData[], headers: string[]): number => {
	const emailHeaderIndex = headers.findIndex(
		(header) => header.toLowerCase().includes('email') || header.toLowerCase().includes('e-mail')
	);

	if (emailHeaderIndex !== -1) return emailHeaderIndex;

	for (let index = 0; index < headers.length; index++) {
		const header = headers[index];
		const hasValidEmail = data.some((row) => isValidEmail(row[header] || ''));
		if (hasValidEmail) return index;
	}

	return -1;
};

export const detectColumnTypes = (
	data: CompedTicketData[],
	headers: string[]
): {
	emailColumn: number;
	firstNameColumn: number;
	lastNameColumn: number;
	quantityColumn: number;
	ticketTypeColumn: number;
} => {
	const emailColumn = detectEmailColumn(data, headers);

	let firstNameColumn = headers.findIndex(
		(header) =>
			header.toLowerCase().includes('first') ||
			header.toLowerCase().includes('fname') ||
			(header.toLowerCase() === 'name' && headers.length > 2)
	);

	if (firstNameColumn === -1 && emailColumn !== -1 && emailColumn + 1 < headers.length) {
		firstNameColumn = emailColumn + 1;
	}

	let lastNameColumn = headers.findIndex(
		(header) =>
			header.toLowerCase().includes('last') ||
			header.toLowerCase().includes('lname') ||
			header.toLowerCase().includes('surname')
	);

	if (lastNameColumn === -1 && firstNameColumn !== -1 && firstNameColumn + 1 < headers.length) {
		lastNameColumn = firstNameColumn + 1;
	}

	let quantityColumn = headers.findIndex((header) => {
		const normalized = header.toLowerCase().replace(/\s+/g, '');
		return (
			normalized.includes('quantity') ||
			normalized.includes('qty') ||
			normalized.includes('amount') ||
			normalized.includes('count')
		);
	});

	if (quantityColumn === -1) {
		headers.forEach((header, index) => {
			const allNumeric = data.every((row) => {
				const value = row[header] || '';
				return value.trim() === '' || isNumeric(value);
			});

			if (allNumeric && data.length > 0) quantityColumn = index;
		});
	}

	const ticketTypeColumn = headers.findIndex((header) => {
		const normalized = header.toLowerCase().replace(/\s+/g, '');
		return (
			normalized.includes('ticket') ||
			normalized === 'type' ||
			normalized === 'tier' ||
			normalized === 'tickettype' ||
			normalized === 'tickettier' ||
			normalized === 'ticketname'
		);
	});

	return {
		emailColumn: Math.max(0, emailColumn),
		firstNameColumn: Math.max(0, firstNameColumn),
		lastNameColumn: Math.max(0, lastNameColumn),
		quantityColumn: Math.max(0, quantityColumn),
		ticketTypeColumn, // -1 when no matching column found
	};
};

export const extractQuantity = (str: string): string | null => {
	const trimmed = str.trim();

	const parenthesesAddMatch = trimmed.match(/^\(\+\s*(\d+)\)$/);
	if (parenthesesAddMatch) {
		const addAmount = parseInt(parenthesesAddMatch[1], 10);
		return (1 + addAmount).toString();
	}

	const flexibleParenthesesMatch = trimmed.match(/\(\+\s*(\d+)\)/);
	if (flexibleParenthesesMatch) {
		const addAmount = parseInt(flexibleParenthesesMatch[1], 10);
		return (1 + addAmount).toString();
	}

	const multiplyMatch = trimmed.match(/^[xX]\s*(\d+)$/i);
	if (multiplyMatch) return multiplyMatch[1];

	const addMatch = trimmed.match(/^\+\s*(\d+)$/);
	if (addMatch) {
		const addAmount = parseInt(addMatch[1], 10);
		return (1 + addAmount).toString();
	}

	if (isNumeric(trimmed)) return trimmed;

	return null;
};

export const generatePlaceholderEmail = (firstName: string, lastName: string): string => {
	const cleanFirstName = firstName.replace(/[^a-zA-Z]/g, '');
	const cleanLastName = lastName.replace(/[^a-zA-Z]/g, '');
	return `hello+${cleanFirstName}${cleanLastName}@punchup.live`;
};

export const processSingleColumnData = (data: CompedTicketData[], headers: string[]): CompedTicketData[] => {
	if (headers.length !== 1) return data;

	const singleColumnName = headers[0];
	const processedData: CompedTicketData[] = [];

	data.forEach((row) => {
		const value = row[singleColumnName] || '';
		const trimmedValue = value.trim();

		if (isValidEmail(trimmedValue)) {
			processedData.push({
				Email: trimmedValue,
				'First Name': '',
				'Last Name': '',
				Quantity: '1',
				'Ticket Type': '',
			});
			return;
		}

		let quantity = '1';
		const fullStringQuantity = extractQuantity(trimmedValue);
		if (fullStringQuantity) quantity = fullStringQuantity;

		const parts = trimmedValue.split(/\s+/).filter((part) => part.length > 0);

		if (parts.length >= 1) {
			const firstName = parts[0];
			let lastName = '';

			if (!fullStringQuantity) {
				for (let i = 1; i < parts.length; i++) {
					const part = parts[i];

					if (part === '+' && i + 1 < parts.length) {
						const nextPart = parts[i + 1];
						if (isNumeric(nextPart)) {
							const addAmount = parseInt(nextPart, 10);
							quantity = (1 + addAmount).toString();
							break;
						}
					}

					const extractedQuantity = extractQuantity(part);
					if (extractedQuantity) {
						quantity = extractedQuantity;
						break;
					}
				}
			}

			const lastNameParts: string[] = [];
			for (let i = 1; i < parts.length; i++) {
				const part = parts[i];
				if (!extractQuantity(part) && part !== '+' && !isNumeric(part)) {
					lastNameParts.push(part);
				}
			}
			lastName = lastNameParts.join(' ');

			processedData.push({
				Email: generatePlaceholderEmail(firstName, lastName),
				'First Name': firstName,
				'Last Name': lastName,
				Quantity: quantity,
				'Ticket Type': '',
			});
		} else {
			processedData.push({
				Email: trimmedValue,
				'First Name': '',
				'Last Name': '',
				Quantity: '1',
				'Ticket Type': '',
			});
		}
	});

	return processedData;
};

/**
 * Auto-detected column mapping for a multi-column file: wraps `detectColumnTypes`
 * and converts its indices into an expected-field → column-index map. This is the
 * default the column-matching UI seeds; the manager can then override any field.
 * `Ticket Type` is `null` when no matching column was found (detection returns -1).
 */
export const buildDefaultMapping = (data: CompedTicketData[], headers: string[]): ColumnMapping => {
	const ct = detectColumnTypes(data, headers);
	return {
		Email: ct.emailColumn,
		'First Name': ct.firstNameColumn,
		'Last Name': ct.lastNameColumn,
		Quantity: ct.quantityColumn,
		'Ticket Type': ct.ticketTypeColumn >= 0 ? ct.ticketTypeColumn : null,
	};
};

/**
 * Collapses parsed rows into the five FIXED_HEADERS keys using an explicit
 * expected-field → column-index mapping. A `null` index (field not in file)
 * yields an empty value. Shared by `processMultiColumnData` (default mapping)
 * and the column-matching UI (user-overridden mapping) so both produce rows the
 * same way (DRY).
 */
export const applyColumnMapping = (
	data: CompedTicketData[],
	headers: string[],
	mapping: ColumnMapping
): CompedTicketData[] => {
	const valueFor = (row: CompedTicketData, field: ExpectedField): string => {
		const index = mapping[field];
		return index !== null && index >= 0 && index < headers.length ? row[headers[index]] ?? '' : '';
	};

	return data.map((row) => {
		const emailValue = valueFor(row, 'Email');
		const firstNameValue = valueFor(row, 'First Name');
		const lastNameValue = valueFor(row, 'Last Name');
		const quantityValue = valueFor(row, 'Quantity') || '1';
		const ticketTypeValue = valueFor(row, 'Ticket Type').trim();

		// Synthesize a placeholder email only when the Email field is mapped to a
		// column but the value is not a valid address (messy-file support). When the
		// manager explicitly unmaps Email (index null), the file has no email column —
		// leave the cell empty rather than inventing a `hello+…@punchup.live` address.
		const emailMapped = mapping.Email !== null;
		const email = isValidEmail(emailValue)
			? emailValue
			: emailMapped
			? generatePlaceholderEmail(firstNameValue, lastNameValue)
			: '';

		return {
			Email: email,
			'First Name': firstNameValue.trim(),
			'Last Name': lastNameValue.trim(),
			Quantity: isNumeric(quantityValue) ? quantityValue : '1',
			'Ticket Type': ticketTypeValue,
		};
	});
};

export const processMultiColumnData = (data: CompedTicketData[], headers: string[]): CompedTicketData[] => {
	if (headers.length <= 1) return data;
	return applyColumnMapping(data, headers, buildDefaultMapping(data, headers));
};

export const processDataWithPlaceholderEmails = (data: CompedTicketData[]): CompedTicketData[] =>
	data.map((row) => {
		const processedRow = { ...row };

		const email = row['Email'] || row['email'] || '';
		const firstName = row['First Name'] || row['first_name'] || '';
		const lastName = row['Last Name'] || row['last_name'] || '';

		if ((!email || email.trim() === '') && firstName && lastName) {
			processedRow['Email'] = generatePlaceholderEmail(firstName, lastName);
		}

		return processedRow;
	});

export const parseExcelFile = async (file: File): Promise<string> => {
	try {
		const data = new Uint8Array(await file.arrayBuffer());
		const workbook = read(data, { type: 'buffer' });
		const sheetName = workbook.SheetNames[0];
		const sheet = workbook.Sheets[sheetName];
		return utils.sheet_to_csv(sheet);
	} catch {
		return '';
	}
};

export const parseCsvData = (csv: string): Promise<{ data: CompedTicketData[]; headers: string[] }> =>
	new Promise((resolve) => {
		Papa.parse<string[]>(csv, {
			skipEmptyLines: true,
			fastMode: true,
			complete: (results) => {
				const parsed: CompedTicketData[] = [];
				const headers: string[] = [];

				if (results.data.length > 0) {
					const isSingleColumn = results.data.every((row) => Array.isArray(row) && row.length === 1);

					if (isSingleColumn) {
						headers.push('Data');

						for (let i = 0; i < results.data.length; i++) {
							const row = results.data[i];
							if (Array.isArray(row) && row.length > 0) {
								const ticketData: CompedTicketData = {};
								ticketData['Data'] = row[0]?.replace(/"/g, '').trim() || '';
								parsed.push(ticketData);
							}
						}
					} else {
						const firstRow = results.data[0];
						const hasHeaderRow = firstRow.some((cell) => {
							const normalized = cell?.toLowerCase().replace(/\s+/g, '') || '';
							return (
								normalized.includes('email') ||
								normalized.includes('first') ||
								normalized.includes('last') ||
								normalized.includes('name') ||
								normalized.includes('quantity') ||
								normalized.includes('qty') ||
								normalized.includes('count')
							);
						});

						let startIndex = 0;

						if (hasHeaderRow) {
							headers.push(...firstRow.map((h) => h?.replace(/"/g, '').trim() || ''));
							startIndex = 1;
						} else {
							const columnCount = firstRow.length;
							if (columnCount === 1) {
								headers.push('Email');
							} else if (columnCount === 2) {
								headers.push('Email', 'First Name');
							} else if (columnCount === 3) {
								headers.push('Email', 'First Name', 'Last Name');
							} else if (columnCount >= 4) {
								headers.push('Email', 'First Name', 'Last Name', 'Quantity');
								for (let i = 4; i < columnCount; i++) {
									headers.push(`Column ${i + 1}`);
								}
							}
							startIndex = 0;
						}

						for (let i = startIndex; i < results.data.length; i++) {
							const row = results.data[i];
							if (Array.isArray(row) && row.length > 0) {
								const ticketData: CompedTicketData = {};
								headers.forEach((header, index) => {
									ticketData[header] = row[index]?.replace(/"/g, '').trim() || '';
								});
								parsed.push(ticketData);
							}
						}
					}
				}

				resolve({ data: parsed, headers });
			},
			error: () => resolve({ data: [], headers: [] }),
		});
	});
